import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';

interface User {
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  isLoading: boolean;        // login em andamento (botão)
  isInitializing: boolean;  // checagem inicial da sessão (spinner de rota)
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  // isInitializing: checagem inicial da sessão (spinner do ProtectedRoute).
  // isLoading: login EM ANDAMENTO (só o botão "Entrando..."). Manter separados é
  // essencial: se o login mexesse no flag do spinner, o ProtectedRoute desmontaria
  // o formulário no meio da tentativa e a mensagem de erro se perderia no remount.
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Verifica sessão ativa salva no localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem('rag_auth_token');
    const savedUser = localStorage.getItem('rag_auth_user');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('rag_auth_token');
        localStorage.removeItem('rag_auth_user');
      }
    }

    setIsInitializing(false);
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true);
      const data = await authApi.login(email, password);

      if (data.success) {
        const userData: User = {
          email: data.user.email,
          name: data.user.name,
          role: data.user.role || 'user',
        };
        setToken(data.token);
        setUser(userData);
        localStorage.setItem('rag_auth_token', data.token);
        localStorage.setItem('rag_auth_user', JSON.stringify(userData));
        return { success: true, message: data.message };
      }

      return { success: false, message: data.message || 'Email ou senha incorretos' };
    } catch (error: any) {
      // Mensagem clara conforme o tipo de falha (em vez de um genérico).
      const status = error?.response?.status;
      if (status === 429) {
        return {
          success: false,
          message: 'Muitas tentativas de login. Aguarde um minuto e tente novamente.',
        };
      }
      const data = error?.response?.data;
      const serverMsg = data?.message || data?.detail || data?.error;
      if (serverMsg) {
        return { success: false, message: String(serverMsg) };
      }
      if (error?.response) {
        return { success: false, message: 'Não foi possível entrar. Tente novamente.' };
      }
      // Sem resposta = rede/servidor fora do ar.
      return {
        success: false,
        message: 'Não foi possível conectar ao servidor. Verifique sua conexão.',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('rag_auth_token');
    localStorage.removeItem('rag_auth_user');
    try {
      authApi.logout();
    } catch {
      // ignora erros no logout — sessão já foi limpa localmente
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    isInitializing,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
